import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import Chart from "chart.js/auto";
import "./Dashboard.css";

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_schools: 0,
    total_students: 0,
    total_plans: 0,

    total_paid_schools: 0,
    total_pending_schools: 0,

    expected_monthly_income: 0,
    received_monthly_income: 0,
    pending_monthly_income: 0,

    recent_schools: [],
    graph_data: { labels: [], income: [], pending: [] },
  });

  const [filter, setFilter] = useState("month");
  const navigate = useNavigate();

  const chartRef = useRef(null);

  // Load Stats
  const loadStats = async () => {
    try {
      const res = await API.get(`/schools/stats/?filter=${filter}`);
      setStats(res.data);
    } catch (err) {
      console.log("Stats load error:", err);
    }
  };

  useEffect(() => {
    loadStats();
  }, [filter]);

  // ======== INIT GRAPH ==========
  useEffect(() => {
    if (!stats?.graph_data?.labels || stats.graph_data.labels.length === 0)
      return;

    const canvas = document.getElementById("incomeGraph");
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: stats.graph_data.labels,
        datasets: [
          {
            label: "Income Received",
            data: stats.graph_data.income,
            backgroundColor: "#2563eb",
            borderRadius: 8,
            barThickness: 24,
          },
          {
            label: "Pending Income",
            data: stats.graph_data.pending,
            backgroundColor: "#f97316",
            borderRadius: 8,
            barThickness: 24,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              color: "#334155",
              boxWidth: 12,
              boxHeight: 12,
              font: { size: 13, weight: "700" },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#64748b", maxRotation: 45, minRotation: 0 },
          },
          y: {
            grid: { color: "#e2e8f0", lineWidth: 1 },
            ticks: { color: "#64748b", stepSize: 10 },
          },
        },
      },
    });
  }, [stats]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-hero">
        <div>
          <span className="dashboard-kicker">Super Admin</span>
          <h2 className="dashboard-title">Dashboard</h2>
          <p>Monitor schools, plans, students, and monthly billing from one place.</p>
        </div>

        <div className="filter-row" aria-label="Analytics range">
          {["day", "week", "month", "year"].map((item) => (
            <button
              key={item}
              onClick={() => setFilter(item)}
              className={filter === item ? "active" : ""}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN STATS GRID */}
      <div className="stats-grid">
        {/* TOTAL SCHOOLS */}
        <div
          className="stat-card stat-card-primary clickable"
          onClick={() => navigate("/schools")}
        >
          <h3>Total Schools</h3>
          <p>{stats.total_schools}</p>
        </div>

        {/* TOTAL STUDENTS */}
        <div className="stat-card">
          <h3>Total Students</h3>
          <p>{stats.total_students}</p>
        </div>

        {/* TOTAL PLANS */}
        <div className="stat-card clickable" onClick={() => navigate("/plans")}>
          <h3>Total Plans</h3>
          <p>{stats.total_plans}</p>
        </div>

        {/* PAID SCHOOLS */}
        <div
          className="stat-card success clickable"
          onClick={() => navigate("/payments")}
        >
          <h3>Paid Schools</h3>
          <p>{stats.total_paid_schools}</p>
        </div>

        {/* PENDING SCHOOLS */}
        <div
          className="stat-card warning clickable"
          onClick={() => navigate("/payments")}
        >
          <h3>Pending Schools</h3>
          <p>{stats.total_pending_schools}</p>
        </div>

        {/* EXPECTED INCOME */}
        <div className="stat-card blue">
          <h3>Expected Income</h3>
          <p>₹{stats.expected_monthly_income}</p>
        </div>

        {/* RECEIVED INCOME */}
        <div className="stat-card green">
          <h3>Received Income</h3>
          <p>₹{stats.received_monthly_income}</p>
        </div>

        {/* PENDING INCOME */}
        <div className="stat-card red">
          <h3>Pending Income</h3>
          <p>₹{stats.pending_monthly_income}</p>
        </div>
      </div>

        {/* GRAPH */}
      <div className="graph-card">
        <div className="panel-heading">
          <div>
            <h3>Income Analytics</h3>
            <p>Received and pending collection trend</p>
          </div>
        </div>
        <div className="chart-box">
          <canvas id="incomeGraph"></canvas>
        </div>
      </div>

      {/* RECENT SCHOOLS */}
      <div className="recent-card">
        <div className="panel-heading">
          <div>
            <h3>Recent Schools</h3>
            <p>Latest school accounts added to the system</p>
          </div>
        </div>

        <table className="recent-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>School Name</th>
              <th>Plan</th>
              <th>Billing</th>
            </tr>
          </thead>

          <tbody>
            {stats.recent_schools.map((s) => (
              <tr key={s.id}>
                <td>{s.id}</td>
                <td>{s.name}</td>
                <td>{s.plan?.name}</td>
                <td>
                  <span className={`billing-tag ${s.billing_status}`}>
                    {s.billing_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
